import { z } from "zod";
import {
  idSchema,
  confidenceSchema,
  contextStatusSchema,
  dimensionResultSchema,
  instantSchema,
  msRangeSchema,
  readinessDimensionSchema,
  readinessSchema,
  sha256Schema,
  sourceDateSchema,
} from "@amber/contracts";

export const READ_CONTRACT_VERSION = "1.0";

export const MISSING_STATES = ["missing", "unknown", "restricted", "inapplicable"] as const;

export function fieldSchema<T extends z.ZodType>(value: T) {
  return z.discriminatedUnion("state", [
    z.strictObject({ state: z.literal("value"), value }),
    z.strictObject({ state: z.literal("missing") }),
    z.strictObject({ state: z.literal("unknown"), reason: z.string().min(1) }),
    z.strictObject({ state: z.literal("restricted") }),
    z.strictObject({ state: z.literal("inapplicable"), reason: z.string().min(1) }),
  ]);
}
export type Field<T> =
  | { state: "value"; value: T }
  | { state: "missing" }
  | { state: "unknown"; reason: string }
  | { state: "restricted" }
  | { state: "inapplicable"; reason: string };

export const cursorSchema = z.string().min(1).max(512);

export const CURSOR_OUTCOMES = ["valid", "expired", "unrestorable", "invalid"] as const;
export const cursorOutcomeSchema = z.enum(CURSOR_OUTCOMES);

export const resyncOutcomeSchema = z.strictObject({
  outcome: z.enum(["expired", "unrestorable", "invalid"]),
  message: z.string().min(1),
  rebuild: z.strictObject({
    snapshot_route: z.literal("POST /machine/v1/snapshots"),
    retained_from_cursor: cursorSchema.nullable(),
  }),
});
export type ResyncOutcome = z.infer<typeof resyncOutcomeSchema>;

const attributionSchema = z.strictObject({
  speaker_ref: z.string().min(1),
  original_mention: fieldSchema(z.string()),
  role: fieldSchema(z.string()),
  assigned_name: fieldSchema(z.string()),
  naming_status: z.enum(["assigned", "ambiguous", "unresolved"]),
  profile_id: fieldSchema(idSchema("prf")),
  profile_decision: z.enum(["matched", "ambiguous", "no_match", "not_applicable"]),
  name_confidence: fieldSchema(confidenceSchema),
  profile_match_confidence: fieldSchema(confidenceSchema),
  unit_attribution_confidence: fieldSchema(confidenceSchema),
  confirmed_by_person: z.boolean(),
  evidence_refs: z.array(z.string()),
  conflicting_evidence_refs: z.array(z.string()),
  alternatives: z.array(
    z.strictObject({ name: z.string(), profile_id: idSchema("prf").nullable(), score: z.number().min(0).max(1) }),
  ),
  decision_version: z.string().min(1),
  calibration_version: fieldSchema(z.string()),
  unresolved_reason: fieldSchema(z.string()),
  validation_outcome: fieldSchema(z.enum(["supported", "contradicted", "insufficient_evidence"])),
});

const locatorSchema = z.strictObject({
  source_version_id: idSchema("ver"),
  artifact_id: fieldSchema(idSchema("art")),
  range_ms: fieldSchema(msRangeSchema),
  text_anchor: fieldSchema(z.string()),
});

export const eventUnitSchema = z.strictObject({
  unit_ref: idSchema("unt"),
  order: z.number().int().nonnegative(),
  kind: z.enum(["turn", "written_unit", "interjection", "unintelligible"]),
  text: fieldSchema(z.string()),
  language: fieldSchema(z.string()),
  translation_of: fieldSchema(idSchema("unt")),
  locator: locatorSchema,
  attribution: attributionSchema,
  eligible_for_profile_ids: z.array(idSchema("prf")),
  flags: z.array(z.string()),
  context_only: z.boolean(),
});

const dimensionSchema = z.strictObject({
  dimension: readinessDimensionSchema,
  result: dimensionResultSchema,
  reason: z.string().nullable(),
});

const passageCheckRefSchema = z.strictObject({
  check_id: idSchema("chk"),
  source_version_id: idSchema("ver"),
  unit_refs: z.array(idSchema("unt")),
  range_ms: msRangeSchema.nullable(),
  method: z.string().min(1),
  outcomes: z.strictObject({ wording: z.string(), locator: z.string(), context: z.string() }),
  verified: z.boolean(),
  invalidated: z.boolean(),
});

export const eventRecordSchema = z.strictObject({
  contract_version: z.literal(READ_CONTRACT_VERSION),
  event_id: idSchema("com"),
  version: z.number().int().positive(),
  revision_id: idSchema("rev"),
  event_kind: z.string().min(1),
  source_type: z.string().min(1),
  change_position: cursorSchema,
  supersedes: fieldSchema(idSchema("rev")),
  merged_into: fieldSchema(idSchema("com")),
  replacement_map: z.array(z.strictObject({ old_ref: z.string(), new_refs: z.array(z.string()) })),
  time: z.strictObject({
    occurrence: fieldSchema(sourceDateSchema),
    published_at: fieldSchema(sourceDateSchema),
    captured_at: fieldSchema(instantSchema),
    updated_at: instantSchema,
  }),
  units: z.array(eventUnitSchema),
  context: z.strictObject({ status: contextStatusSchema, surrounding_unit_refs: z.array(idSchema("unt")) }),
  provenance: z.strictObject({
    publisher: fieldSchema(z.string()),
    platform: z.string().min(1),
    original_url: fieldSchema(z.url()),
    source_version_ids: z.array(idSchema("ver")),
    artifacts: z.array(
      z.strictObject({ artifact_id: idSchema("art"), kind: z.string(), sha256: sha256Schema, media_ref: z.string() }),
    ),
    lineage: z.array(z.string()),
  }),
  rights: z.strictObject({
    declared_terms: fieldSchema(z.string()),
    permission: fieldSchema(z.string()),
    restrictions: z.array(z.string()),
    unknowns: z.array(z.string()),
    observed_at: fieldSchema(instantSchema),
    origin_availability: z.enum(["available", "changed", "removed", "unknown"]),
    archive_access: z.enum(["available", "restricted", "deleted"]),
  }),
  readiness: z.strictObject({
    policy: z.strictObject({ id: idSchema("pol"), name: z.string(), version: z.number().int().positive() }),
    per_person: z.array(
      z.strictObject({
        profile_id: idSchema("prf"),
        result: readinessSchema,
        reason: z.string().nullable(),
        dimensions: z.array(dimensionSchema).length(8),
      }),
    ),
    result: readinessSchema,
    reason: z.string().nullable(),
    passage_checks: z.array(passageCheckRefSchema),
  }),
});
export type EventRecord = z.infer<typeof eventRecordSchema>;

export const CHANGE_KINDS = [
  "published",
  "withdrawn",
  "restored",
  "invalidated",
  "relationship",
  "availability",
  "reassessed",
] as const;
export const changeKindSchema = z.enum(CHANGE_KINDS);

export const changeEntrySchema = z.strictObject({
  position: cursorSchema,
  kind: changeKindSchema,
  event_id: idSchema("com"),
  revision_id: idSchema("rev").nullable(),
  version: z.number().int().positive().nullable(),
  committed_at: instantSchema,
  notice: z.string().nullable(),
  scope: z.strictObject({ unit_refs: z.array(idSchema("unt")), profile_ids: z.array(idSchema("prf")) }).nullable(),
  replacement_pending: z.boolean(),
});
export type ChangeEntry = z.infer<typeof changeEntrySchema>;

export const MAX_CHANGES_PAGE = 500;

export const changesQuerySchema = z.strictObject({
  after: cursorSchema,
  limit: z.coerce.number().int().min(1).max(MAX_CHANGES_PAGE).default(100),
});

export const changesPageSchema = z.discriminatedUnion("status", [
  z.strictObject({
    status: z.literal("ok"),
    entries: z.array(changeEntrySchema),
    next_cursor: cursorSchema,
    has_more: z.boolean(),
  }),
  z.strictObject({ status: z.literal("resync_required"), resync: resyncOutcomeSchema }),
]);

export const snapshotPageSchema = z.strictObject({
  snapshot_id: z.string().min(1),
  as_of_cursor: cursorSchema,
  continuation_cursor: cursorSchema,
  records: z.array(eventRecordSchema),
  withdrawn_event_ids: z.array(idSchema("com")),
  next_page: z.string().nullable(),
});

export const READ_FAILURES = ["temporarily_unavailable", "unsupported_input", "not_found", "restricted"] as const;

export const readFailureSchema = z.strictObject({
  failure: z.enum(READ_FAILURES),
  message: z.string().min(1),
  notice: z.string().nullable(),
});

export const withdrawnNoticeSchema = z.strictObject({
  event_id: idSchema("com"),
  state: z.literal("withdrawn"),
  notice: z.string().min(1),
  change_position: cursorSchema,
});

export const MAX_EVENTS_PER_READ = 50;

export const eventsQuerySchema = z.strictObject({
  ids: z
    .string()
    .transform((s) => s.split(",").filter((id) => id.length > 0))
    .pipe(z.array(idSchema("com")).min(1).max(MAX_EVENTS_PER_READ)),
});

export const eventQuerySchema = z.strictObject({ version: z.coerce.number().int().positive().optional() });

export const eventsResponseSchema = z.strictObject({
  records: z.array(eventRecordSchema),
  failures: z.array(z.strictObject({ event_id: z.string(), failure: readFailureSchema })),
});

export const MAX_SNAPSHOT_PAGE = 50;

export const snapshotQuerySchema = z.strictObject({
  page: z.string().min(1).max(200).optional(),
  page_size: z.coerce.number().int().min(1).max(MAX_SNAPSHOT_PAGE).default(MAX_SNAPSHOT_PAGE),
});

export const resyncResponseSchema = z.strictObject({
  status: z.literal("resync_required"),
  resync: resyncOutcomeSchema,
});

export const PASSAGE_CHECK_DIMENSIONS = ["wording", "locator", "context"] as const;
const checkOutcomeSchema = z.enum(["passed", "failed", "insufficient", "not_run"]);

export const passageCheckRequestSchema = z.strictObject({
  event_id: idSchema("com"),
  unit_refs: z.array(idSchema("unt")).min(1).max(20),
  source_version_id: idSchema("ver"),
  context: z.enum(["surrounding_turns", "passage_only"]).default("surrounding_turns"),
  dimensions: z
    .array(z.enum(PASSAGE_CHECK_DIMENSIONS))
    .min(1)
    .max(3)
    .default([...PASSAGE_CHECK_DIMENSIONS]),
  idempotency_key: z.string().min(8).max(128),
  reason: z.string().min(1).max(500).optional(),
});
export type PassageCheckRequest = z.infer<typeof passageCheckRequestSchema>;

export const passageCheckStatusSchema = z.strictObject({
  check_id: idSchema("chk"),
  state: z.enum(["requested", "running", "completed", "failed_to_run"]),
  event_id: idSchema("com"),
  revision_id: idSchema("rev"),
  unit_refs: z.array(idSchema("unt")),
  context_unit_refs: z.array(idSchema("unt")),
  source_version_id: idSchema("ver"),
  claimed_source_version_id: idSchema("ver"),
  range_ms: msRangeSchema.nullable(),
  context: z.enum(["surrounding_turns", "passage_only"]),
  policy: z.string().min(1),
  method: z.string().min(1),
  outcomes: z.strictObject({ wording: checkOutcomeSchema, locator: checkOutcomeSchema, context: checkOutcomeSchema }),
  details: z.strictObject({ wording: z.string(), locator: z.string(), context: z.string() }).nullable(),
  verified: z.boolean(),
  failure_detail: z.string().nullable(),
  invalidated: z.strictObject({ at: instantSchema, cause: z.string() }).nullable(),
  requested_at: instantSchema,
  completed_at: instantSchema.nullable(),
  reused: z.boolean(),
  note: z.string(),
});
export type PassageCheckStatus = z.infer<typeof passageCheckStatusSchema>;
